/**
 * Where a send that came back to the message box waits for a page reload
 * (mission stream contract phase 6, agion/Agion#2327). A message the host
 * refused or never confirmed goes back into the message box with its text and
 * files; this keeps what the box then holds, so a reload of the same
 * conversation puts it back until the person sends it again or empties the
 * box (useComposerReturns).
 *
 * One record per user and conversation (mission), in the tab's
 * sessionStorage: it survives a reload of the tab and ends with the tab.
 * Files are kept as references with upload provenance and display fields,
 * never their bytes: the files are already on
 * the host, and the message box stages them again from there. Opening a
 * conversation forgets every other user's records, and signing out forgets
 * all of them (services/auth.ts).
 *
 * Storage can be off or full (private mode, quota); every call then does
 * nothing and the send comes back only while the page stays open, as before.
 */

import type { HITLAttachment } from '../types/streaming'

/** What the message box held when a send came back, and since. */
export interface KeptSend {
  text: string
  attachments: HITLAttachment[]
}

const PREFIX = 'agion.chat.failedSend.v1:'

function keyOf(userId: string, missionId: string): string {
  return `${PREFIX}${encodeURIComponent(userId)}:${encodeURIComponent(missionId)}`
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/** The keys this module wrote that `matches` selects. */
function ownKeys(store: Storage, matches: (key: string) => boolean): string[] {
  const keys: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(PREFIX) && matches(key)) keys.push(key)
  }
  return keys
}

function isAttachment(value: unknown): value is HITLAttachment {
  const a = value as Partial<HITLAttachment> | null
  return !!a && typeof a.host_file_id === 'string' && a.host_file_id !== '' && typeof a.filename === 'string'
    && typeof a.content_type === 'string' && typeof a.size === 'number'
    && (a.host_id === undefined || typeof a.host_id === 'string')
}

/**
 * The send kept for this user in this conversation, or null. Forgets every
 * other user's records first, so a tab that changes hands keeps none.
 */
export function loadFailedSend(userId: string, missionId: string): KeptSend | null {
  const store = storage()
  if (!store) return null
  try {
    const own = `${PREFIX}${encodeURIComponent(userId)}:`
    for (const key of ownKeys(store, (k) => !k.startsWith(own))) store.removeItem(key)
    const raw = store.getItem(keyOf(userId, missionId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<KeptSend>
    const text = typeof parsed.text === 'string' ? parsed.text : ''
    const attachments = Array.isArray(parsed.attachments) ? parsed.attachments.filter(isAttachment) : []
    return text.trim() || attachments.length ? { text, attachments } : null
  } catch {
    return null
  }
}

export function saveFailedSend(userId: string, missionId: string, kept: KeptSend): void {
  try {
    storage()?.setItem(keyOf(userId, missionId), JSON.stringify({
      text: kept.text,
      attachments: kept.attachments,
    }))
  } catch {
    // Full or off: the send stays in the message box while the page is open.
  }
}

export function forgetFailedSend(userId: string, missionId: string): void {
  try {
    storage()?.removeItem(keyOf(userId, missionId))
  } catch {
    // Off: nothing was kept.
  }
}

/** Forgets every kept send in this tab, for every user: at sign-out. */
export function forgetAllFailedSends(): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of ownKeys(store, () => true)) store.removeItem(key)
  } catch {
    // Off: nothing was kept.
  }
}
